import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { changePassword } from "@/api/users";
import { useAuth } from "@/auth/AuthProvider";
import { PasswordInput } from "@/components/PasswordInput";

const MIN_LEN = 8;

export function ChangePasswordPage() {
  const nav = useNavigate();
  const { me, markPasswordChanged, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      markPasswordChanged();
      nav("/admin", { replace: true });
    },
    onError: (e: Error) => setError(e.message),
  });

  const forced = !!me?.must_change_password;

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md card p-6 space-y-4">
        <div>
          <h1 className="text-lg font-semibold">Change your password</h1>
          {forced && (
            <p className="mt-1 text-sm text-amber-600">
              You must change your password before continuing.
            </p>
          )}
        </div>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (next.length < MIN_LEN) {
              setError(`New password must be at least ${MIN_LEN} characters.`);
              return;
            }
            if (next !== confirm) {
              setError("New passwords do not match.");
              return;
            }
            if (next === current) {
              setError("New password must be different from the current one.");
              return;
            }
            mutation.mutate();
          }}
        >
          <div className="space-y-1">
            <label className="block text-xs uppercase tracking-wide text-slate-500">
              {forced ? "Temporary password" : "Current password"}
            </label>
            <PasswordInput
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs uppercase tracking-wide text-slate-500">New password</label>
            <PasswordInput
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={MIN_LEN}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs uppercase tracking-wide text-slate-500">Confirm new password</label>
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={MIN_LEN}
            />
          </div>
          {error && <div className="alert-danger text-xs">{error}</div>}
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-slate-700"
              onClick={() => {
                void logout();
              }}
            >
              Sign out
            </button>
            <button
              type="submit"
              className="btn-primary text-sm px-3 py-1.5"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Saving…" : "Update password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
